/**
 * ActivityLoginTab — 2026-09-25 (user request).
 * Tab "Aktivitas & Login" di Performance Dashboard: durasi kerja & jumlah login
 * per user (harian/mingguan/bulanan/keseluruhan) sebagai dasar KPI.
 * Nama bisa diklik → dialog detail history & durasi per login.
 * Visibilitas difilter backend: diri sendiri, atasan satu divisi, HRGA,
 * BOD Lv1, pemegang perm session_history.view.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, X, LogIn, Clock3 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { fmtDuration } from "@/lib/constants";
import { DivisionCell, SubdivisionCell, OrgLines } from "@/components/performance/OrgIdentity";

const PERIODS = [
  { k: "day", label: "Harian" },
  { k: "week", label: "Mingguan" },
  { k: "month", label: "Bulanan" },
  { k: "all", label: "Total Keseluruhan" },
];
const DAYS_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shiftAnchor(iso, period, dir) {
  const d = new Date(iso + "T00:00:00");
  if (period === "day") d.setDate(d.getDate() + dir);
  else if (period === "week") d.setDate(d.getDate() + dir * 7);
  else if (period === "month") d.setMonth(d.getMonth() + dir);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTime(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return "-"; }
}

function fmtDateID(isoDate) {
  try {
    const d = new Date(isoDate.length === 10 ? isoDate + "T00:00:00" : isoDate);
    return `${DAYS_ID[d.getDay()]}, ${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return isoDate; }
}

function shortDevice(ua) {
  if (!ua) return "-";
  if (/mobile|android|iphone/i.test(ua)) return "Mobile";
  if (/windows/i.test(ua)) return "Windows";
  if (/macintosh|mac os/i.test(ua)) return "Mac";
  if (/linux/i.test(ua)) return "Linux";
  return "Lainnya";
}

const REASON_CLS = {
  "Logout": "bg-slate-100 text-slate-700 border-slate-300",
  "Idle Timeout": "bg-amber-50 text-amber-700 border-amber-300",
  "Ditimpa login baru": "bg-orange-50 text-orange-700 border-orange-300",
  "Aktif sekarang": "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export default function ActivityLoginTab() {
  const [period, setPeriod] = useState("day");
  const [anchor, setAnchor] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailRow, setDetailRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/activity-summary", { params: { period, anchor } });
      setData(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat data aktivitas");
      setData(null);
    } finally { setLoading(false); }
  }, [period, anchor]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.users || [];
  const totLogins = rows.reduce((s, r) => s + r.login_count, 0);
  const totDur = rows.reduce((s, r) => s + r.duration_sec, 0);

  return (
    <div className="space-y-4" data-testid="perf-activity-tab">
      {/* Period selector + navigasi tanggal */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex border border-slate-300" role="tablist" aria-label="Periode">
          {PERIODS.map((p) => (
            <button key={p.k} type="button" role="tab" aria-selected={period === p.k}
              onClick={() => setPeriod(p.k)}
              data-testid={`perf-act-period-${p.k}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors ${period === p.k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period !== "all" && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setAnchor((a) => shiftAnchor(a, period, -1))} data-testid="perf-act-prev" aria-label="Periode sebelumnya"
              className="p-1.5 border border-slate-300 hover:bg-slate-50"><ChevronLeft size={14} /></button>
            <span className="text-xs font-semibold text-[#0B1D3A] min-w-[180px] text-center tabular-nums" data-testid="perf-act-range">
              {period === "day" ? fmtDateID(anchor) : (data?.range_label || anchor)}
            </span>
            <button type="button" onClick={() => setAnchor((a) => shiftAnchor(a, period, 1))} disabled={anchor >= todayIso()} data-testid="perf-act-next" aria-label="Periode berikutnya"
              className="p-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-40"><ChevronRight size={14} /></button>
            <button type="button" onClick={() => setAnchor(todayIso())} disabled={anchor === todayIso()} data-testid="perf-act-today"
              className="px-2 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 disabled:opacity-40">
              Hari Ini
            </button>
          </div>
        )}
        <button type="button" onClick={load} disabled={loading} data-testid="perf-act-refresh" aria-label="Muat ulang"
          className="p-1.5 border border-slate-300 hover:bg-slate-50 ml-auto">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Ringkasan agregat */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 px-4 py-3">
          <div className="overline text-[9px] text-slate-500">Total Login</div>
          <div className="font-display text-2xl text-[#0B1D3A] tabular-nums" data-testid="perf-act-total-logins">{totLogins}</div>
        </div>
        <div className="bg-white border border-slate-200 px-4 py-3">
          <div className="overline text-[9px] text-slate-500">Total Durasi Kerja</div>
          <div className="font-display text-2xl text-[#0B1D3A] tabular-nums" data-testid="perf-act-total-duration">{fmtDuration(totDur)}</div>
        </div>
        <div className="bg-white border border-slate-200 px-4 py-3">
          <div className="overline text-[9px] text-slate-500">User Tercakup</div>
          <div className="font-display text-2xl text-[#0B1D3A] tabular-nums" data-testid="perf-act-total-users">{rows.length}</div>
        </div>
      </div>

      <p className="text-[10px] text-slate-500 italic">
        Sesi tanpa logout eksplisit diestimasi maksimal {data?.idle_cap_minutes ?? 30} menit (batas idle-timeout) dan ditandai <b>~</b>. Klik nama untuk detail history per login.
      </p>

      {/* Tabel per user */}
      <section className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left overline text-slate-600 text-[10px]">
              <th className="px-4 py-2.5">Nama</th>
              <th className="px-4 py-2.5">Divisi</th>
              <th className="px-4 py-2.5">Sub-divisi</th>
              <th className="px-4 py-2.5">Lv</th>
              <th className="px-4 py-2.5 text-right">Login</th>
              <th className="px-4 py-2.5 text-right">Total Durasi</th>
              <th className="px-4 py-2.5 text-right">Hari Aktif</th>
            </tr>
          </thead>
          <tbody data-testid="perf-act-table">
            {loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-slate-400 italic">Memuat data aktivitas…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-slate-400 italic" data-testid="perf-act-empty">Tidak ada data pada periode ini.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perf-act-row-${r.user_id}`}>
                <td className="px-4 py-2.5">
                  <button type="button" onClick={() => setDetailRow(r)} data-testid={`perf-act-user-${r.user_id}`}
                    className="font-medium text-[#0B1D3A] hover:text-[#FF5A1F] underline decoration-dotted underline-offset-4 text-left">
                    {r.name}
                  </button>
                </td>
                <DivisionCell row={r} className="px-4 py-2.5" testid={`perf-act-division-${r.user_id}`} />
                <SubdivisionCell row={r} className="px-4 py-2.5" testid={`perf-act-subdivision-${r.user_id}`} />
                <td className="px-4 py-2.5 text-slate-600 text-xs">Lv{r.level}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  <span className="inline-flex items-center gap-1"><LogIn size={11} className="text-slate-400" />{r.login_count}</span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#0B1D3A]" data-testid={`perf-act-dur-${r.user_id}`}>
                  {r.estimated_count > 0 && <span className="text-amber-600" title={`${r.estimated_count} sesi estimasi`}>~</span>}
                  {fmtDuration(r.duration_sec)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.active_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {detailRow && <ActivityDetailDialog row={detailRow} onClose={() => setDetailRow(null)} />}
    </div>
  );
}

function ActivityDetailDialog({ row, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/performance/activity-user/${row.user_id}`);
      setData(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat detail aktivitas");
    } finally { setLoading(false); }
  }, [row.user_id]);
  useEffect(() => { load(); }, [load]);

  const sessions = data?.sessions || [];
  const daily = data?.daily || [];
  const totDur = sessions.reduce((s, x) => s + x.duration_sec, 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="perf-act-dialog">
      <div className="bg-white border border-slate-200 shadow-2xl w-full max-w-4xl mt-8">
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div>
            <p className="overline text-[9px] text-slate-500">Aktivitas & Login · Detail</p>
            <h2 className="font-display text-lg text-[#0B1D3A]" data-testid="perf-act-dialog-name">{row.name}</h2>
            <div className="mt-1 flex items-start gap-3">
              <OrgLines row={row} testid="perf-act-dialog-org" />
              <span className="text-[10px] text-slate-500">Lv{row.level}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="perf-act-dialog-close" aria-label="Tutup"
            className="p-1.5 border border-slate-300 hover:bg-slate-100"><X size={13} /></button>
        </div>

        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">Memuat history…</div>
          ) : !data ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">Data tidak tersedia.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Total Sesi</div>
                  <div className="font-display text-xl tabular-nums" data-testid="perf-act-dialog-sessions">{sessions.length}</div>
                </div>
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Total Durasi</div>
                  <div className="font-display text-xl tabular-nums" data-testid="perf-act-dialog-duration">{fmtDuration(totDur)}</div>
                </div>
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Hari Aktif</div>
                  <div className="font-display text-xl tabular-nums">{daily.length}</div>
                </div>
              </div>

              {/* Rekap harian */}
              <section>
                <h3 className="overline text-[10px] text-slate-500 mb-1.5">Rekap Per Hari (WIB)</h3>
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Tanggal</th>
                      <th className="px-2 py-1.5 text-right">Login</th>
                      <th className="px-2 py-1.5 text-right">Durasi</th>
                    </tr>
                  </thead>
                  <tbody data-testid="perf-act-dialog-daily">
                    {daily.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-slate-400 italic">Belum ada aktivitas.</td></tr>}
                    {daily.slice(0, 31).map((d) => (
                      <tr key={d.date} className="border-t border-slate-100">
                        <td className="px-2 py-1.5">{fmtDateID(d.date)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{d.login_count}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                          {d.estimated_count > 0 && <span className="text-amber-600">~</span>}{fmtDuration(d.duration_sec)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* History per login */}
              <section>
                <h3 className="overline text-[10px] text-slate-500 mb-1.5">History Per Login (terbaru dulu)</h3>
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Tanggal</th>
                      <th className="px-2 py-1.5 text-left">Login</th>
                      <th className="px-2 py-1.5 text-left">Berakhir</th>
                      <th className="px-2 py-1.5 text-right">Durasi</th>
                      <th className="px-2 py-1.5 text-left">Status Akhir</th>
                      <th className="px-2 py-1.5 text-left">IP</th>
                      <th className="px-2 py-1.5 text-left">Device</th>
                    </tr>
                  </thead>
                  <tbody data-testid="perf-act-dialog-sessions-table">
                    {sessions.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-slate-400 italic">Belum ada sesi login.</td></tr>}
                    {sessions.map((s, i) => (
                      <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`perf-act-session-${i}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtDateID(s.login_at)}</td>
                        <td className="px-2 py-1.5 tabular-nums whitespace-nowrap">{fmtTime(s.login_at)}</td>
                        <td className="px-2 py-1.5 tabular-nums whitespace-nowrap">{fmtTime(s.end_at)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap">
                          {s.estimated && <span className="text-amber-600" title="Estimasi">~</span>}{fmtDuration(s.duration_sec)}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border ${REASON_CLS[s.end_reason_label] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                            {s.end_reason_label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{s.ip_masked || "-"}</td>
                        <td className="px-2 py-1.5 text-slate-500" title={s.user_agent}>{shortDevice(s.user_agent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-slate-400 italic mt-1.5">
                  <Clock3 size={10} className="inline mr-1" />
                  Durasi bertanda ~ adalah estimasi (sesi ditutup tanpa logout eksplisit, di-cap {data.idle_cap_minutes} menit sejak aktivitas terakhir).
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
